import useWebSocket from 'common/hooks/useWebSocket';
import { some } from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// Shorter than the Patient screen's 5s (useLineItemUpdates): this screen locks its actions while a
// refresh is pending, and one order produces far smaller bursts than a patient's whole order list.
// Long enough to fold the per-line-item events of a single write into one reload.
export const ORDER_REFRESH_DEBOUNCE_MS = 1500;

/**
 * Refreshes the Order screen when another user changes this order (POM-6313).
 *
 * Listens to the same `line_item_updated` event the Patient screen's Action Items tab uses. The
 * websocket server broadcasts it to every connected client, so each event is filtered down to
 * this order — by orderId, falling back to the order's line item ids — and the viewer's own
 * writes are skipped, since the action that made them already reloads the screen.
 *
 * Returns `refreshPending`, true from the moment a relevant event arrives until the refresh has
 * landed, so the screen can lock actions that would otherwise run against data known to be stale.
 */
const useOrderSocketRefresh = ({ order, reloading, onRefresh }) => {
  const { socket, isConnected } = useWebSocket();
  const { enqueueSnackbar } = useSnackbar();
  const currentUserId = useSelector((state) => state.auth?.userId);
  const [refreshPending, setRefreshPending] = useState(false);

  const orderId = order?.orderId;

  // Read through refs so a reload (new order object, new callbacks) doesn't resubscribe and
  // drop a pending refresh.
  const lineItemsRef = useRef(order?.lineItems);
  const reloadingRef = useRef(reloading);
  const onRefreshRef = useRef(onRefresh);
  const currentUserIdRef = useRef(currentUserId);
  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  lineItemsRef.current = order?.lineItems;
  reloadingRef.current = reloading;
  onRefreshRef.current = onRefresh;
  currentUserIdRef.current = currentUserId;
  enqueueSnackbarRef.current = enqueueSnackbar;

  useEffect(() => {
    if (!socket || !isConnected || !orderId) {
      return undefined;
    }

    let timer = null;
    let disposed = false;

    const refresh = () => {
      // A reload already in flight may have been issued before this change landed. Wait for it
      // rather than stacking a second one: reloadOrder only resolves its latest caller.
      if (reloadingRef.current) {
        timer = setTimeout(refresh, ORDER_REFRESH_DEBOUNCE_MS);
        return;
      }
      timer = null;
      Promise.resolve(onRefreshRef.current?.())
        .then(() =>
          enqueueSnackbarRef.current(`Order ${orderId} has been modified`, { variant: 'info' })
        )
        .finally(() => {
          // Another event may have arrived while this refresh ran; stay locked for that one.
          if (!timer && !disposed) setRefreshPending(false);
        });
    };

    const isRelevant = (eventData) => {
      if (eventData.orderId) {
        return String(eventData.orderId) === String(orderId);
      }
      return some(eventData.lineItemIds, (lineItemId) =>
        some(lineItemsRef.current, { lineItemId })
      );
    };

    const eventHandler = (data) => {
      const payload = Array.isArray(data) ? data[0] : data;
      const eventData = payload?.message || payload;
      if (!eventData) return;

      const { userId } = eventData;
      if (userId && String(userId) === String(currentUserIdRef.current)) return;
      if (!isRelevant(eventData)) return;

      clearTimeout(timer);
      timer = setTimeout(refresh, ORDER_REFRESH_DEBOUNCE_MS);
      setRefreshPending(true);
    };

    socket.on('line_item_updated', eventHandler);
    return () => {
      disposed = true;
      clearTimeout(timer);
      setRefreshPending(false);
      socket.off('line_item_updated', eventHandler);
    };
  }, [socket, isConnected, orderId]);

  return { refreshPending };
};

export default useOrderSocketRefresh;
